/**
 * CommunityHeader
 *
 * Domain-specific wrapper that handles V1/V2 feature flag switching for community headers.
 * Uses the generic PageHeader from the Design System when V2 is enabled.
 *
 * Usage (simple - header with actions):
 * ```tsx
 * <CommunityHeader
 *   v1Component={<ExistingHeaderComponent {...props} />}
 *   v2Props={{ title, subtitle }}
 *   v2Children={<Button>Action</Button>}
 * />
 * ```
 *
 * Usage (complex - full V2 layout):
 * ```tsx
 * <CommunityHeader
 *   v1Component={<ExistingHeaderComponent {...props} />}
 *   v2Component={
 *     <>
 *       <PageHeader title="Title">
 *         <Button>Action</Button>
 *       </PageHeader>
 *       <AdditionalContent />
 *     </>
 *   }
 * />
 * ```
 *
 * ## Pattern Selection Guide
 *
 * | Pattern | When to Use | Example |
 * |---------|-------------|---------|
 * | `v2Props` only | Title-only headers with no actions | Audit detail, moderation topic detail |
 * | `v2Props` + `v2Children` | Headers with action buttons | Community page, community groups list |
 * | `v2Component` | Complex layouts needing full control | Community group detail with custom sections |
 */

import { FEATURE_FLAGS } from '@core/domain/featureFlags';
import { useIsFeatureEnabled } from '@core/hooks';
import { PageHeader, type PageHeaderProps } from '@soteria/design-system-ve';
import type { ReactElement, ReactNode } from 'react';

export interface CommunityHeaderProps {
  /**
   * The existing V1 component to render when feature flag is disabled
   */
  v1Component: ReactElement;

  /**
   * Props to pass to the V2 PageHeader component when feature flag is enabled.
   * Use this for simple cases where V2 is just the PageHeader component.
   * Mutually exclusive with v2Component.
   */
  v2Props?: Omit<PageHeaderProps, 'children'>;

  /**
   * Children (action buttons) to pass to PageHeader when using v2Props.
   * These become the children of the PageHeader component.
   */
  v2Children?: ReactNode;

  /**
   * Full V2 component tree to render when feature flag is enabled.
   * Use this for complex cases where V2 includes additional layout changes.
   * Mutually exclusive with v2Props.
   */
  v2Component?: ReactElement;
}

/**
 * Hook to check if Community Header V2 is enabled
 * Centralizes the feature flag check for reuse
 */
export function useCommunityHeaderV2() {
  return useIsFeatureEnabled(FEATURE_FLAGS.COMMUNITY_HEADER_V2);
}

/**
 * Domain-specific wrapper that switches between V1 and V2 community headers
 * based on the COMMUNITY_HEADER_V2 feature flag.
 * V2 uses the generic PageHeader from the Design System.
 */
export function CommunityHeader({ v1Component, v2Props, v2Children, v2Component }: CommunityHeaderProps) {
  const isV2Enabled = useCommunityHeaderV2();

  if (isV2Enabled) {
    // If full v2Component provided, use it; otherwise use v2Props with PageHeader
    if (v2Component) {
      return v2Component;
    }

    if (v2Props) {
      return <PageHeader {...v2Props}>{v2Children}</PageHeader>;
    }

    // Fallback: V2 enabled but neither v2Props nor v2Component provided.
    // Log warning and render V1 to avoid crashing the component tree.
    console.warn('CommunityHeader: V2 feature flag is enabled but neither v2Props nor v2Component was provided. Falling back to V1 rendering.');

    return v1Component;
  }

  return v1Component;
}
